import React from 'react';
import { getSupabaseClient } from '../supabaseClient';

type DashboardStats = {
  totalUsers: number;
  totalBooks: number;
  totalRequests: number;
  activeRequests: number;
  completedRequests: number;
  popularCategories: { category: string; count: number }[];
  recentActivity: { type: string; description: string; timestamp: string }[];
};

export default function Dashboard() {
  const supabase = getSupabaseClient();
  const [stats, setStats] = React.useState<DashboardStats | null>(null);
  const [loading, setLoading] = React.useState(true);

  const loadStats = React.useCallback(async () => {
    setLoading(true);
    try {
      // Load basic counts
      const [
        { count: usersCount },
        { count: booksCount },
        { count: requestsCount },
        { count: activeRequestsCount },
        { count: completedRequestsCount },
      ] = await Promise.all([
        supabase.from('users').select('*', { count: 'exact', head: true }),
        supabase.from('books').select('*', { count: 'exact', head: true }),
        supabase.from('requests').select('*', { count: 'exact', head: true }),
        supabase.from('requests').select('*', { count: 'exact', head: true }).neq('status', 'closed').neq('status', 'returned'),
        supabase.from('requests').select('*', { count: 'exact', head: true }).eq('status', 'closed'),
      ]);

      // Load popular categories
      const { data: categoriesData } = await supabase
        .from('books')
        .select('category')
        .not('category', 'is', null);

      const categoryCounts = categoriesData?.reduce((acc: Record<string, number>, book: any) => {
        if (book.category) {
          acc[book.category] = (acc[book.category] || 0) + 1;
        }
        return acc;
      }, {} as Record<string, number>) || {};

      const popularCategories = Object.entries(categoryCounts)
        .map(([category, count]) => ({ category, count: count as number }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 5);

      // Load recent activity (simplified - in real app, you'd have an activity log table)
      const recentActivity = [
        { type: 'user', description: 'New user registered', timestamp: new Date().toISOString() },
        { type: 'book', description: 'New book added', timestamp: new Date(Date.now() - 3600000).toISOString() },
        { type: 'request', description: 'Book request submitted', timestamp: new Date(Date.now() - 7200000).toISOString() },
      ];

      setStats({
        totalUsers: usersCount || 0,
        totalBooks: booksCount || 0,
        totalRequests: requestsCount || 0,
        activeRequests: activeRequestsCount || 0,
        completedRequests: completedRequestsCount || 0,
        popularCategories,
        recentActivity,
      });
    } catch (error) {
      console.error('Failed to load dashboard stats:', error);
    } finally {
      setLoading(false);
    }
  }, [supabase]);

  React.useEffect(() => {
    loadStats();
  }, [loadStats]);

  if (loading) {
    return (
      <div>
        <h1 className="page-header">
          Admin Dashboard
        </h1>
        <div className="page-subtitle">
          Loading statistics...
        </div>
        <div className="card">
          <div>Loading...</div>
        </div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div>
        <h1 className="page-header">
          Admin Dashboard
        </h1>
        <div className="card">
          <div>Failed to load dashboard data</div>
          <button onClick={loadStats} style={{ marginTop: '12px' }}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <h1 className="page-header">
        Admin Dashboard
      </h1>
      <div className="page-subtitle">
        Overview of Shiriki platform statistics
      </div>

      {/* Statistics Cards */}
      <div className="stats-grid">
        <div className="card">
          <h3 className="stats-card-label">
            Total Users
          </h3>
          <div className="stats-card-value stats-card-value-primary">
            {stats.totalUsers}
          </div>
        </div>

        <div className="card">
          <h3 className="stats-card-label">
            Total Books
          </h3>
          <div className="stats-card-value stats-card-value-success">
            {stats.totalBooks}
          </div>
        </div>

        <div className="card">
          <h3 className="stats-card-label">
            Total Requests
          </h3>
          <div className="stats-card-value stats-card-value-warning">
            {stats.totalRequests}
          </div>
        </div>

        <div className="card">
          <h3 className="stats-card-label">
            Active Requests
          </h3>
          <div className="stats-card-value stats-card-value-info">
            {stats.activeRequests}
          </div>
        </div>
      </div>

      <div className="two-column-grid">
        {/* Popular Categories */}
        <div className="card">
          <h2>Popular Categories</h2>
          {stats.popularCategories.length > 0 ? (
            <div>
              {stats.popularCategories.map((item, _index) => (
                <div key={item.category} className="list-item">
                  <span>{item.category}</span>
                  <span className="list-item-value">{item.count}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="muted">No category data available</div>
          )}
        </div>

        {/* Recent Activity */}
        <div className="card">
          <h2>Recent Activity</h2>
          {stats.recentActivity.length > 0 ? (
            <div>
              {stats.recentActivity.map((activity, index) => (
                <div key={index} className="activity-item">
                  <div className="activity-description">{activity.description}</div>
                  <div className="activity-timestamp">
                    {new Date(activity.timestamp).toLocaleString()}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="muted">No recent activity</div>
          )}
        </div>
      </div>
    </div>
  );
}